import { supabase } from "@/lib/supabase";
import {
  DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS,
  STALE_SESSION_HOURS,
} from "@/lib/geoDistance";
import { setActiveWorkSession } from "@/lib/cleanerWorkSession";

export const WORK_SESSION_MAX_DISPLAY_HOURS = 16;

export type WorkSessionStatus = "open" | "closed" | "auto_closed";
export type WorkSessionKind = "cleaning" | "snow_removal" | "extra_paid";
export type WorkSessionCloseReason =
  | "manual"
  | "geofence"
  | "stale_timeout"
  | "resume_prompt"
  | "coordinator_edit";

export type CleaningWorkSession = {
  id: string;
  org_id: string;
  location_id: string;
  user_id: string;
  task_id: string | null;
  session_kind: WorkSessionKind;
  started_at: string;
  ended_at: string | null;
  status: WorkSessionStatus;
  close_reason: WorkSessionCloseReason | null;
  needs_review: boolean;
  is_paid: boolean;
  check_in_lat: number | null;
  check_in_lng: number | null;
  check_out_lat: number | null;
  check_out_lng: number | null;
  check_out_distance_meters: number | null;
  remaining_tasks: number | null;
  edited_by: string | null;
  edited_at: string | null;
  edit_reason: string | null;
  original_started_at: string | null;
  original_ended_at: string | null;
};

export type OpenWorkSession = CleaningWorkSession & {
  location_address?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
  auto_checkout_distance_meters?: number;
};

const SESSION_COLUMNS =
  "id, org_id, location_id, user_id, task_id, session_kind, started_at, ended_at, status, close_reason, needs_review, is_paid, check_in_lat, check_in_lng, check_out_lat, check_out_lng, check_out_distance_meters, remaining_tasks, edited_by, edited_at, edit_reason, original_started_at, original_ended_at";

export function workSessionCloseReasonLabel(reason: WorkSessionCloseReason | null): string {
  switch (reason) {
    case "manual":
      return "Ręczne zakończenie";
    case "geofence":
      return "Automatyczne wylogowanie (odległość)";
    case "stale_timeout":
      return "Automatyczne wylogowanie (timeout 8h)";
    case "resume_prompt":
      return "Zakończone po powrocie do aplikacji";
    case "coordinator_edit":
      return "Korekta koordynatora / właściciela";
    default:
      return "Brak";
  }
}

export function isAutoClosedSession(session: Pick<CleaningWorkSession, "status" | "close_reason">): boolean {
  return session.status === "auto_closed" || session.close_reason === "geofence" || session.close_reason === "stale_timeout";
}

export function sessionDurationMinutes(startedAt: string, endedAt: string): number {
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
  return Math.round((end - start) / (1000 * 60));
}

export async function fetchOrgAutoCheckoutDistanceMeters(orgId: string): Promise<number> {
  const { data, error } = await supabase
    .from("organizations")
    .select("auto_checkout_distance_meters")
    .eq("id", orgId)
    .maybeSingle();
  if (error) {
    console.warn("[workSessions] Nie udało się pobrać progu auto-wylogowania:", error);
    return DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS;
  }
  const value = (data as { auto_checkout_distance_meters?: number } | null)?.auto_checkout_distance_meters;
  return typeof value === "number" && value > 0 ? value : DEFAULT_AUTO_CHECKOUT_DISTANCE_METERS;
}

export async function fetchOpenWorkSessions(userId: string, orgId: string): Promise<OpenWorkSession[]> {
  const { data, error } = await supabase
    .from("cleaning_work_sessions")
    .select(`${SESSION_COLUMNS}, cleaning_locations(address, latitude, longitude)`)
    .eq("user_id", userId)
    .eq("org_id", orgId)
    .eq("status", "open")
    .order("started_at", { ascending: false });

  if (error) {
    console.error("[workSessions] fetchOpenWorkSessions:", error);
    throw error;
  }

  const threshold = await fetchOrgAutoCheckoutDistanceMeters(orgId);
  return (data ?? []).map((row) => {
    const loc = Array.isArray(row.cleaning_locations)
      ? row.cleaning_locations[0]
      : row.cleaning_locations;
    const { cleaning_locations: _ignored, ...session } = row as CleaningWorkSession & {
      cleaning_locations?: { address?: string; latitude?: number | null; longitude?: number | null } | null;
    };
    return {
      ...session,
      location_address: loc?.address ?? null,
      location_lat: loc?.latitude ?? null,
      location_lng: loc?.longitude ?? null,
      auto_checkout_distance_meters: threshold,
    };
  });
}

export async function startWorkSession(input: {
  orgId: string;
  locationId: string;
  userId: string;
  taskId: string | null;
  latitude: number | null;
  longitude: number | null;
  sessionKind?: WorkSessionKind;
}): Promise<CleaningWorkSession | null> {
  const sessionKind = input.sessionKind ?? "cleaning";
  const existing = await fetchOpenWorkSessions(input.userId, input.orgId);
  const sameLocation = existing.find(
    (session) =>
      session.location_id === input.locationId &&
      (session.session_kind ?? "cleaning") === sessionKind
  );
  if (sameLocation) return sameLocation;

  const payload = {
    org_id: input.orgId,
    location_id: input.locationId,
    user_id: input.userId,
    task_id: input.taskId,
    session_kind: sessionKind,
    started_at: new Date().toISOString(),
    status: "open" as const,
    check_in_lat: input.latitude,
    check_in_lng: input.longitude,
  };

  const { data, error } = await supabase
    .from("cleaning_work_sessions")
    .insert(payload)
    .select(SESSION_COLUMNS)
    .single();

  if (error) {
    console.error("[workSessions] startWorkSession:", error);
    throw error;
  }

  if (sessionKind === "cleaning") {
    setActiveWorkSession(input.locationId, true);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("domio-work-session-changed", { detail: { locationId: input.locationId, active: true } }));
    }
  }
  return data as CleaningWorkSession;
}

export async function closeWorkSession(input: {
  sessionId: string;
  locationId: string;
  reason: WorkSessionCloseReason;
  latitude?: number | null;
  longitude?: number | null;
  distanceMeters?: number | null;
  remainingTasks?: number | null;
  userId?: string;
  taskId?: string | null;
}): Promise<void> {
  const isAuto = input.reason === "geofence" || input.reason === "stale_timeout";
  const endedAt =
    input.reason === "stale_timeout"
      ? undefined
      : new Date().toISOString();

  const updatePayload: Record<string, unknown> = {
    status: isAuto ? "auto_closed" : "closed",
    close_reason: input.reason,
    needs_review: isAuto || input.reason === "resume_prompt",
    check_out_lat: input.latitude ?? null,
    check_out_lng: input.longitude ?? null,
    check_out_distance_meters: input.distanceMeters ?? null,
    remaining_tasks: input.remainingTasks ?? null,
  };

  if (endedAt) {
    updatePayload.ended_at = endedAt;
  } else {
    const { data: current } = await supabase
      .from("cleaning_work_sessions")
      .select("started_at")
      .eq("id", input.sessionId)
      .maybeSingle();
    if (current?.started_at) {
      const start = new Date(current.started_at).getTime();
      updatePayload.ended_at = new Date(start + STALE_SESSION_HOURS * 60 * 60 * 1000).toISOString();
    } else {
      updatePayload.ended_at = new Date().toISOString();
    }
  }

  const { error } = await supabase
    .from("cleaning_work_sessions")
    .update(updatePayload)
    .eq("id", input.sessionId)
    .eq("status", "open");

  if (error) {
    console.error("[workSessions] closeWorkSession:", error);
    throw error;
  }

  if (input.userId && input.taskId) {
    const { error: logError } = await supabase.from("task_execution_logs").insert({
      task_id: input.taskId,
      user_id: input.userId,
      action_type: "CHECK_OUT",
      notes: `auto_checkout; reason=${input.reason}; GPS: ${input.latitude ?? "null"}, ${input.longitude ?? "null"}; distance_m=${input.distanceMeters ?? "n/a"}`,
    });
    if (logError) {
      console.warn("[workSessions] CHECK_OUT log failed:", logError);
    }
  }

  const { data: remaining } = await supabase
    .from("cleaning_work_sessions")
    .select("id")
    .eq("location_id", input.locationId)
    .eq("status", "open")
    .eq("session_kind", "cleaning")
    .maybeSingle();
  const cleaningStillOpen = Boolean(remaining);
  setActiveWorkSession(input.locationId, cleaningStillOpen);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("domio-work-session-changed", { detail: { locationId: input.locationId, active: cleaningStillOpen } }));
  }
}

export async function closeStaleWorkSessions(): Promise<number> {
  const { data, error } = await supabase.rpc("close_stale_cleaning_work_sessions");
  if (error) {
    console.warn("[workSessions] close_stale_cleaning_work_sessions:", error);
    return 0;
  }
  return typeof data === "number" ? data : 0;
}

export async function updateWorkSessionTimes(input: {
  sessionId: string;
  startedAt: string;
  endedAt: string;
  editorId: string;
  reason: string;
}): Promise<void> {
  const { data: current, error: fetchError } = await supabase
    .from("cleaning_work_sessions")
    .select("started_at, ended_at, original_started_at, original_ended_at, is_paid")
    .eq("id", input.sessionId)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!current) throw new Error("Nie znaleziono sesji");
  if (current.is_paid) throw new Error("Sesja została już rozliczona");

  const { error } = await supabase
    .from("cleaning_work_sessions")
    .update({
      started_at: input.startedAt,
      ended_at: input.endedAt,
      status: "closed",
      close_reason: "coordinator_edit",
      edited_by: input.editorId,
      edited_at: new Date().toISOString(),
      edit_reason: input.reason,
      original_started_at: current.original_started_at ?? current.started_at,
      original_ended_at: current.original_ended_at ?? current.ended_at,
      needs_review: false,
    })
    .eq("id", input.sessionId);

  if (error) throw error;
}
